/**
 * Layout for a single foundation run: header (Back to Foundations) + sub-nav (Target | TFD | ALO | Foundational Symbols) + outlet.
 * Used at /dashboard/brand-strategy/foundations/:runId. Sub-nav shows when you click into a run from the Foundations dashboard.
 */

import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom'
import { FileText, Loader2, MoreHorizontal } from 'lucide-react'
import { ROUTES } from '@/constants/routes'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CampaignPageHeader } from '@/modules/campaigns/components'
import { ProcessInterviewControl } from '@/modules/interview-coach/ProcessInterviewControl'
import { ProcessInterviewMenuItem } from '@/modules/interview-coach/ProcessInterviewMenuItem'
import { EditableFoundationBaselineName } from './EditableFoundationBaselineName'
import { FoundationsSubNav } from './FoundationsSubNav'
import { SubjectStackCrossLinks, type SubjectStackType } from '@/modules/campaigns/components/SubjectStackCrossLinks'
import { FoundationPasteImportModal } from './modals/FoundationPasteImportModal'
import { FoundationPasteImportBanner } from './FoundationPasteImportBanner'
import { FoundationPasteImportProvider, useFoundationPasteImport } from '../context/FoundationPasteImportContext'
import { persuasionLabService } from '@/services/persuasion-lab'
import { useAuthContext } from '@/context'
import { ChannelProvider } from 'ably/react'
import { scopedOrWorkspacePath, subjectScope } from '@/modules/subjects/scope-navigation'
import {
    foundationsStepFromPath,
    setFoundationsRecent,
} from '../utils/foundations-recent-context'

const FoundationsRunLayoutBody: FC<{
    refetchRunRef: React.MutableRefObject<() => void>
}> = ({ refetchRunRef }) => {
    const { runId, subjectType, subjectId } = useParams<{ runId: string; subjectType?: string; subjectId?: string }>()
    const scope = subjectScope(subjectType, subjectId)
    const navigate = useNavigate()
    const location = useLocation()
    const { orgAccount } = useAuthContext()
    const [runName, setRunName] = useState<string>('')
    const [processInterviewOpen, setProcessInterviewOpen] = useState(false)
    const [runRefreshKey, setRunRefreshKey] = useState(0)
    const [run, setRun] = useState<any>(null)
    const { syncFromRunMetadata, openModal, isImportActive } = useFoundationPasteImport()

    const refetchRun = useCallback(() => {
        if (!runId) return
        persuasionLabService
            .getRun(runId)
            .then((data: any) => {
                setRun(data || null)
                setRunName(data?.name || `Foundation run`)
                syncFromRunMetadata(data)
            })
            .catch(() => navigate(ROUTES.PERSUASION_LAB_FOUNDATIONS, { replace: true }))
    }, [runId, navigate, syncFromRunMetadata])

    useEffect(() => {
        refetchRunRef.current = refetchRun
    }, [refetchRun, refetchRunRef])

    useEffect(() => {
        refetchRun()
    }, [refetchRun])

    const handleInterviewApplyComplete = useCallback(() => {
        setRunRefreshKey(current => current + 1)
        refetchRunRef.current()
    }, [refetchRunRef])

    useEffect(() => {
        if (!runId || !run) return
        const { stepId, stepLabel } = foundationsStepFromPath(location.pathname, runId)
        setFoundationsRecent(orgAccount?.org?.id, {
            runId,
            runName: run.name || runName || 'Brand baseline',
            subjectName: (run as any).cpp_anchor_name,
            anchorType: run.cpp_anchor_type,
            stepId,
            stepLabel,
            pathname: location.pathname,
        })
    }, [runId, run, runName, location.pathname, orgAccount?.org?.id])

    const headerSubtitle = (() => {
        const subject = (run as any)?.cpp_anchor_name?.trim()
        const baseline = runName?.trim()
        if (subject && baseline && subject.toLowerCase() !== baseline.toLowerCase()) {
            return `${subject} · ${baseline}`
        }
        return subject || baseline || 'Brand baseline'
    })()

    const anchorType = run?.cpp_anchor_type as SubjectStackType | undefined
    const anchorId = run?.cpp_anchor_id ? String(run.cpp_anchor_id) : undefined
    const showSubjectCrossLinks =
        Boolean(runId && anchorType && anchorId && ['person', 'company', 'product'].includes(anchorType))

    const headerTitle =
        run && runId ? (
            <EditableFoundationBaselineName
                variant='header'
                run={{ ...run, _id: runId, name: runName || run.name }}
                onRenamed={(_id, name) => {
                    setRunName(name)
                    setRun((prev: any) => (prev ? { ...prev, name } : prev))
                }}
            />
        ) : (
            'Loading…'
        )

    return (
        <div className='min-h-screen bg-background'>
            {/* Single sticky stack: avoids sub-nav offset jumping over the paste-import banner */}
            <div className='sticky top-0 z-30'>
                <CampaignPageHeader
                    title={headerTitle}
                    subtitle={headerSubtitle}
                    onBack={() => navigate(scopedOrWorkspacePath(scope, ROUTES.PERSUASION_LAB_FOUNDATIONS, 'foundations'))}
                    showContinue={false}
                    sticky={false}
                >
                    {runId ? (
                        <ProcessInterviewControl
                            targetProfileId={runId}
                            targetProfileType='foundation'
                            profileName={run?.name || runName || 'Foundation profile'}
                            open={processInterviewOpen}
                            onOpenChange={setProcessInterviewOpen}
                            showTrigger={false}
                            showStatus
                            onApplyComplete={handleInterviewApplyComplete}
                        />
                    ) : null}
                    {runId ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type='button'
                                    size='sm'
                                    variant='outline'
                                    className='h-9 w-9 border-slate-200 p-0 text-slate-600'
                                    aria-label='More actions'
                                >
                                    <MoreHorizontal className='h-4 w-4' />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end' className='w-52'>
                                <ProcessInterviewMenuItem onSelect={() => setProcessInterviewOpen(true)} />
                                <DropdownMenuItem
                                    onSelect={() => openModal(isImportActive ? 'progress' : 'guide')}
                                >
                                    {isImportActive ? (
                                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                    ) : (
                                        <FileText className='mr-2 h-4 w-4' />
                                    )}
                                    {isImportActive ? 'View import progress' : 'Build from pasted text'}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : null}
                </CampaignPageHeader>
                {showSubjectCrossLinks && anchorType && anchorId && runId ? (
                    <div className='border-b border-slate-200 bg-slate-50/90 px-4 sm:px-6 py-2'>
                        <SubjectStackCrossLinks
                            subjectType={anchorType}
                            subjectId={anchorId}
                            activeSurface='foundation'
                            foundationRunId={runId}
                            compact
                        />
                    </div>
                ) : null}
                <FoundationsSubNav sticky={false} />
            </div>
            <FoundationPasteImportBanner />
            <main className='w-full min-w-0 max-w-7xl mx-auto py-4 sm:py-6 lg:py-8 px-4 sm:px-6 overflow-x-hidden'>
                <Outlet context={{ runRefreshKey }} />
            </main>
            {runId && (
                <FoundationPasteImportModal runId={runId} run={run} onComplete={refetchRun} />
            )}
        </div>
    )
}

export const FoundationsRunLayout: FC = () => {
    const { runId } = useParams<{ runId: string }>()
    const refetchRunRef = useRef<() => void>(() => {})

    return (
        <ChannelProvider channelName={`run:${runId || 'none'}`}>
            <FoundationPasteImportProvider
                runId={runId || ''}
                onImportComplete={() => refetchRunRef.current()}
            >
                <FoundationsRunLayoutBody refetchRunRef={refetchRunRef} />
            </FoundationPasteImportProvider>
        </ChannelProvider>
    )
}
